import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { CV_BANK_SOURCE_LABELS, type CvBankDuplicateChoice, type CvBankDuplicatePair, type CvBankEntryView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { CvViewer } from '../../../components/cv/CvViewer';
import { formatDate } from '../../../lib/format';
import { keepLabel, pairReason } from '../cv-bank-format';
import styles from '../CvBank.module.css';

interface DuplicateChooserProps {
  readonly pair: CvBankDuplicatePair;
  readonly canEdit: boolean;
  readonly onChoose: (keep: CvBankDuplicateChoice) => void;
}

/**
 * ADR 0046 Q4 — two CV Bank entries that may be one person, side by side, and three buttons:
 * keep the first, keep the second, or keep both. The one not kept leaves the bank; the person
 * and their history stay. Merging two records stays on CAND-06's Merge screen.
 */
export function DuplicateChooser({ pair, canEdit, onChoose }: DuplicateChooserProps): ReactNode {
  const [isShowingCvs, setIsShowingCvs] = useState(false);
  return (
    <section className={styles.pair} aria-label={`Possible duplicate: ${pair.first.candidate.fullName} and ${pair.second.candidate.fullName}`}>
      <p className={styles.pairWhy}>{pairReason(pair)}</p>
      <div className={styles.sides}>
        <Side entry={pair.first} />
        <Side entry={pair.second} />
      </div>
      {isShowingCvs && (
        <div className={styles.viewers}>
          <div className={styles.viewer}>
            <CvViewer cv={pair.first.cv} candidateName={pair.first.candidate.fullName} fallback="Compare from the details above." />
          </div>
          <div className={styles.viewer}>
            <CvViewer cv={pair.second.cv} candidateName={pair.second.candidate.fullName} fallback="Compare from the details above." />
          </div>
        </div>
      )}
      <div className={styles.choices}>
        {canEdit && (
          <>
            <Button size="sm" variant="outline" onClick={() => onChoose('first')}>
              {keepLabel(pair.first)}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onChoose('second')}>
              {keepLabel(pair.second)}
            </Button>
            <Button size="sm" variant="solid" onClick={() => onChoose('both')}>
              Keep both
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" onClick={() => setIsShowingCvs((current) => !current)}>
          {isShowingCvs ? 'Hide the CVs' : 'Show both CVs'}
        </Button>
      </div>
    </section>
  );
}

function Side({ entry }: { readonly entry: CvBankEntryView }): ReactNode {
  const contact = [entry.candidate.email, entry.candidate.phone].filter((value) => value !== null && value !== '').join(' · ');
  return (
    <div className={styles.side}>
      <p className={styles.sideName}>
        <Link to="/candidates/$candidateId" params={{ candidateId: entry.candidate.id }} className={styles.name}>
          {entry.candidate.fullName}
        </Link>
      </p>
      <p className={styles.sideLine}>{contact === '' ? '—' : contact}</p>
      <p className={styles.sideLine}>{entry.cv.fileName}</p>
      <p className={styles.sideLine}>
        Added {formatDate(entry.addedAt)}
        {entry.addedByName ? ` by ${entry.addedByName}` : ''} · {CV_BANK_SOURCE_LABELS[entry.source]}
      </p>
    </div>
  );
}
